import React from 'react'
import styled, {css} from 'styled-components'
import {CircleAlert} from 'lucide-react'
import {Tooltip} from '../BaseComponents'
import {InfoHint} from './InfoHint'
import {EllipsisText} from './Table'
import {BarTrack, type BarTone, type BarTrackSize} from './internal/BarTrack'
import {isBlankNode, StatValue, statValueState} from './internal/StatValue'

export type {BarTone} from './internal/BarTrack'

/**
 * BarList
 *
 * A horizontal bar list — each row is a label, a proportional track + fill,
 * and (optionally) a value. Generalizes the recurring dashboard patterns:
 * unfilled-order aging buckets, active-employees-by-site, top-branch rankings,
 * and top-client share bars. Fill width = value / max (default max = the
 * largest value, so the top item is full-width). Colors come from the
 * `--viz-*` scale (same `DonutTone` set as DonutCard) — no new tokens.
 *
 * Bare-list states, for a list that sits inside a section rather than its own
 * card (BarListCard gets its states from DashboardCard instead): `loading` keeps
 * each row's label with a skeleton track and marks the list `aria-busy`; `error`
 * replaces the rows with an error line. Omit both and the list renders exactly as
 * before they existed.
 *
 * Options for a compact figure list (the reliability parts):
 * - `size="sm"`: a `--space-2` track, a label column of at least `--space-24` (wider when a label
 *   needs it) and caption-size text, with the label and value columns aligned across rows;
 * - `rowTestId`: each row's `data-testid` (default `bar-{id}`); its hint is `${rowTestId}-info`;
 * - `valueTone`: a warning or error bar's value takes `--warning-800` / `--error-800`;
 * - per-item `hint`: an InfoHint beside the label;
 * - per-item states, by the shared precedence loading > error > pending > value (see StatValue):
 *   `loading` gives the row a skeleton track and value, `error` reads 'Couldn’t load', and
 *   `pending` shows its text ('Not tracked yet', 'Pending') in place of the value. A row in any of
 *   these draws no fill. A `pendingReason` joins the hint, or makes the text its own InfoHint
 *   trigger when the row has no hint: one trigger per row either way.
 *
 * @example aging buckets
 *   <BarList
 *     items={[
 *       { label: '< 2 hours',  value: 4, tone: 'cyan' },
 *       { label: '2 – 8 hours',value: 3, tone: 'blue' },
 *       { label: '8 – 24 hours',value: 2, tone: 'navy' },
 *       { label: '> 24 hours', value: 1, tone: 'navy-soft' },
 *     ]}
 *   />
 *
 * @example client share (custom value label, fixed denominator)
 *   <BarList max={totalHours} items={clients.map(c => (
 *     { label: c.name, value: c.hours, valueLabel: `${c.sharePct}%` }
 *   ))} />
 *
 * @example holding its place while the values load
 *   <BarList loading={isLoading} error={Boolean(error)}
 *     items={buckets.map(b => ({ label: b.label, value: counts?.[b.key] ?? 0 }))} />
 *
 * @example reliability parts: compact, toned values, per-row states
 *   <BarList size="sm" max={100} valueTone
 *     rowTestId={(item) => `staffer-metrics-reliability-part-${item.id}`}
 *     items={[
 *       { id: 'fillRatio', label: 'Fill ratio', value: 94, tone: 'navy', hint: definition },
 *       { id: 'noShow', label: 'No-show', value: 76, tone: 'warning' },
 *       { id: 'overPledge', label: 'Over-pledge', value: 0, pending: 'Not tracked yet' },
 *     ]} />
 */

export type BarListSize = BarTrackSize

export interface BarItem {
  label: string
  /**
   * A stable identity for the row, used as its React key and in its default `data-testid`
   * (`bar-{id}`). Defaults to `label`. Pass one when labels can repeat (e.g. people's names), or
   * rows collide.
   */
  id?: string
  value: number
  /** Bar color — `--viz-*` scale or a semantic status tone (default cyan). */
  tone?: BarTone
  /** Override the displayed value (e.g. `'36%'`); defaults to `value`. */
  valueLabel?: React.ReactNode
  /** An InfoHint beside the label (`${rowTestId}-info`), e.g. the part's definition. */
  hint?: React.ReactNode
  /** The hint's accessible name (default `About ${label}`). */
  hintLabel?: string
  /** This row's value is not known yet: a skeleton track and value. */
  loading?: boolean
  /** Truthy = this row's value could not be read: 'Couldn’t load'. */
  error?: Error | null | boolean
  /** This row's error text (default: the list's `errorLabel`). */
  errorLabel?: string
  /** Text in place of the value, e.g. 'Not tracked yet' or 'Pending'. */
  pending?: React.ReactNode
  /** Why the row reads its pending text. */
  pendingReason?: React.ReactNode
}

export interface BarListProps {
  items: BarItem[]
  /** Denominator for fill width; default = the largest item value. */
  max?: number
  /** Render the value column (default true). */
  showValue?: boolean
  /** `md` (default) or `sm`, the compact list (see above). */
  size?: BarListSize
  /** Each row's `data-testid`. Default `bar-${id}`. */
  rowTestId?: (item: BarItem) => string
  /** Colour a warning or error bar's value with its status ink (`--*-800`). */
  valueTone?: boolean
  /**
   * The values are not known yet: each row keeps its label, a skeleton fills its track, no value
   * shows, and the list is marked `aria-busy`. Pass the labels you expect so the list holds its
   * shape. Wins over `error`.
   */
  loading?: boolean
  /**
   * The values could not be read (truthy = error): no rows, only `errorLabel` in error text with an
   * icon named "Error". The same type as DashboardCard's `error`, and it must stay so:
   * BarListCardProps extends both. BarListCard consumes `loading`, `error` and `errorLabel` for its
   * own card states and never passes them on to the list.
   */
  error?: Error | null | boolean
  /** The error line's text, and a failed row's unless it names its own (default "Couldn’t load"). */
  errorLabel?: string
  'data-testid'?: string
}

/**
 * The compact list's label column: at least the mock's 96px (`.ep-fac`), and as wide as the widest
 * label with its hint, so 'Response time' beside an InfoHint is not cut short.
 */
const SM_LABEL_COLUMN = 'minmax(var(--space-24), max-content)'

/**
 * `sm` is one grid whose rows are subgrids, so the value column is as wide as the widest value
 * ('Not tracked yet' beside '94') and every bar starts and ends at the same place.
 */
const List = styled.div<{$size: BarListSize; $showValue: boolean}>`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);

  ${({$size, $showValue}) =>
    $size === 'sm' &&
    css`
      display: grid;
      grid-template-columns: ${$showValue
        ? `${SM_LABEL_COLUMN} minmax(0, 1fr) auto`
        : `${SM_LABEL_COLUMN} minmax(0, 1fr)`};
      column-gap: var(--space-3);
      row-gap: var(--space-2);
    `}
`

const Row = styled.div<{$size: BarListSize; $showValue: boolean}>`
  display: grid;
  align-items: center;

  ${({$size, $showValue}) =>
    $size === 'sm'
      ? css`
          grid-column: 1 / -1;
          grid-template-columns: subgrid;
        `
      : css`
          grid-template-columns: ${$showValue
            ? 'minmax(80px, 140px) 1fr auto'
            : 'minmax(80px, 140px) 1fr'};
          gap: var(--space-3);
        `}
`

/* The row's label column, which may shrink below its text. The text inside cuts a long label
   with "…", and only then does its tooltip show the whole label (STFFLG-4236). */
const Label = styled.span`
  min-width: 0;
  overflow: hidden;
`

const LabelText = styled(EllipsisText)<{$size: BarListSize}>`
  font-size: ${({$size}) => ($size === 'sm' ? 'var(--fs-caption)' : 'var(--fs-body)')};
  font-weight: ${({$size}) => ($size === 'sm' ? 'var(--fw-regular)' : 'var(--fw-medium)')};
  color: var(--text-primary);
`

/** A label with its hint beside it. */
const LabelCell = styled.span`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
`

const Value = styled.span<{$size: BarListSize; $ink: string | null}>`
  text-align: right;
  font-variant-numeric: tabular-nums;

  ${({$size, $ink}) =>
    $size === 'sm'
      ? css`
          font-size: var(--fs-caption);
          font-weight: var(--fw-bold);
          color: ${$ink ?? 'var(--primary-800)'};
        `
      : css`
          font-family: var(--font-mono);
          font-size: var(--fs-body);
          font-weight: var(--fw-semibold);
          color: ${$ink ?? 'var(--text-primary)'};
        `}
`

/** Small status text is `--*-800` (§3a). */
const ErrorNote = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--fs-body);
  color: var(--error-800);

  svg {
    flex-shrink: 0;
  }
`

/** A value's status ink, when `valueTone` is on and the bar is a warning or an error. */
const VALUE_INK: Partial<Record<BarTone, string>> = {
  warning: 'var(--warning-800)',
  error: 'var(--error-800)',
}

export function BarList({
  items,
  max,
  showValue = true,
  size = 'md',
  rowTestId,
  valueTone = false,
  loading = false,
  error,
  errorLabel = 'Couldn’t load',
  'data-testid': dataTestId,
}: BarListProps) {
  const withValue = showValue && !loading

  if (!loading && error) {
    return (
      <List $size={size} $showValue={withValue} data-testid={dataTestId}>
        <ErrorNote>
          <CircleAlert size="1em" role="img" aria-label="Error" />
          {errorLabel}
        </ErrorNote>
      </List>
    )
  }

  const denom = max ?? Math.max(...items.map((i) => i.value), 1)
  const busy = loading || items.some((item) => Boolean(item.loading))

  return (
    <List
      $size={size}
      $showValue={withValue}
      data-testid={dataTestId}
      aria-busy={busy || undefined}>
      {items.map((item) => {
        const width = denom > 0 ? Math.min(100, (item.value / denom) * 100) : 0
        const id = item.id ?? item.label
        const testId = rowTestId ? rowTestId(item) : `bar-${id}`
        const tone = item.tone ?? 'cyan'
        const shown = item.valueLabel ?? item.value
        const state = statValueState({
          value: shown,
          loading: item.loading,
          error: item.error,
          pending: item.pending,
        })
        // Only a figure draws a fill; a row that is loading, failed or pending has none.
        const hasFigure = state === 'value' || state === 'empty'

        const hasHint = !isBlankNode(item.hint)
        const hintLabel = item.hintLabel ?? `About ${item.label}`
        const infoTestId = `${testId}-info`
        const reason =
          state === 'pending' && !isBlankNode(item.pendingReason) ? item.pendingReason : null
        // One trigger per row: a row with a hint carries its pending reason there.
        const hintContent =
          hasHint && reason !== null ? (
            <>
              {item.hint} {reason}
            </>
          ) : (
            item.hint
          )

        return (
          <Row key={id} $size={size} $showValue={withValue} data-testid={testId}>
            {hasHint ? (
              <LabelCell>
                <Label>
                  <Tooltip content={item.label} block>
                    <LabelText $size={size}>{item.label}</LabelText>
                  </Tooltip>
                </Label>
                <InfoHint label={hintLabel} content={hintContent} data-testid={infoTestId} />
              </LabelCell>
            ) : (
              <Label>
                <Tooltip content={item.label} block>
                  <LabelText $size={size}>{item.label}</LabelText>
                </Tooltip>
              </Label>
            )}
            <BarTrack
              size={size}
              tone={tone}
              loading={loading || state === 'loading'}
              percent={hasFigure ? width : null}
            />
            {withValue && (
              <Value
                $size={size}
                $ink={valueTone && state === 'value' ? VALUE_INK[tone] ?? null : null}>
                <StatValue
                  value={shown}
                  loading={item.loading}
                  error={item.error}
                  errorLabel={item.errorLabel ?? errorLabel}
                  pending={item.pending}
                  pendingReason={hasHint ? undefined : item.pendingReason}
                  reasonLabel={hintLabel}
                  reasonTestId={infoTestId}
                />
              </Value>
            )}
          </Row>
        )
      })}
    </List>
  )
}
